import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import DebounceInput from "../../components/debounceInput/DebounceInput";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/joy/IconButton";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import Typography from "@mui/joy/Typography";
import Avatar from "@mui/joy/Avatar";

import {NotificationService} from "../../common/notification";
import EditorDialog from "./EditorDialog";
import AddIcon from '@mui/icons-material/Add';
import {gamesListApi, upsertGameApi} from "./gamesApi";
import {Game} from "../../models/games";

export function GamesListTab() {
    const [games, setGames] = React.useState<Game[]>([]);
    const [filterSearch, setFilterSearch] = React.useState('');
    const [editorOpen, setEditorOpen] = React.useState(false);

    const [selectedGame, setSelectedGame] = React.useState<Game>({
        id: 0,
        contextId: 0,
        inventoryLimit: 300,
        orderItemsLimit: 30,
        name: '',
        icon: '',
        alias: '',
        isNew: true,
        isActive:true,
        sortOrder: 0,
        offlineBidsEnabled: false,
        offlineBidsActivatePercent: 50,
        offlineBidsDeactivatePercent: 70
    });
    const [loading, setLoading] = React.useState(false);
    const id = React.useRef(0);

    React.useEffect(() => {
        loadData().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const edit = (game: Game) => {
        setSelectedGame(game);
        setEditorOpen(true);
    }

    const onSave = async (game: Game, file?: File) => {
        setLoading(true)
        try {
            const res = await upsertGameApi({game, file})
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            setLoading(false)
            id.current = 0;
        }
    }

    return (
        <React.Fragment>
            {editorOpen &&
                <EditorDialog game={selectedGame}
                              onConfirm={onSave}
                              open={editorOpen}
                              setOpen={setEditorOpen}/>
            }
            <Sheet
                className="SearchAndFilters-mobile"
                sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
            >
                <DebounceInput
                    size="sm"
                    placeholder="Поиск"
                    startDecorator={<SearchIcon/>}
                    sx={{flexGrow: 1}}
                    debounceTimeout={1000}
                    handleDebounce={(val) => setFilterSearch(val)}
                />
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    onClick={() => edit({
                        id: 0,
                        contextId: 0,
                        inventoryLimit: 300,
                        orderItemsLimit: 30,
                        name: '',
                        icon: '',
                        alias: '',
                        isNew: true,
                        isActive:true,
                        sortOrder: 0,
                        offlineBidsEnabled: false,
                        offlineBidsActivatePercent: 50,
                        offlineBidsDeactivatePercent: 70
                    })}
                >
                    <AddIcon/>
                </IconButton>
            </Sheet>
            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'none', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Поиск игры</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Поиск"
                        startDecorator={<SearchIcon/>}
                        debounceTimeout={1000}
                        handleDebounce={(val) => setFilterSearch(val)}
                    />
                </FormControl>
                <FormControl size="sm" sx={{
                    justifyContent: 'end'
                }}>
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => edit({
                            id: 0,
                            contextId: 0,
                            inventoryLimit: 300,
                            orderItemsLimit: 30,
                            name: '',
                            icon: '',
                            alias: '',
                            isNew: true,
                            isActive:true,
                            sortOrder: 0,
                            offlineBidsEnabled: false,
                            offlineBidsActivatePercent: 50,
                            offlineBidsDeactivatePercent: 70
                        })}
                    >
                        <AddIcon/>
                    </IconButton>
                </FormControl>
            </Box>
            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                            border: 'none',
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 50, padding: '12px 6px'}}></th>
                                <th style={{width: 120, padding: '12px 6px'}}>Id</th>
                                <th style={{width: 220, padding: '12px 6px'}}>Название / Роут</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Порядок</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Context Id</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Лимит инвентаря</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Лимит на заказ</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Офлайн заявки</th>
                                <th style={{width: 120, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {games.filter(p => filterSearch === '' || p.name.includes(filterSearch)).map((row) => (
                                <tr key={row.id} style={!row.isActive ? {backgroundColor: 'rgba(229,72,72,0.2)'} : {}}>
                                    <td>
                                        <Avatar
                                            variant="outlined"
                                            size="sm"
                                            src={row.icon}
                                        />
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.id}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.name} / {row.alias}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.sortOrder}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.contextId}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.inventoryLimit}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.orderItemsLimit ?? '(не установлен)'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.offlineBidsEnabled ? 'Вкл' : 'Выкл'}</Typography>
                                    </td>
                                    <td>
                                        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                            <Link level="body-sm" component="button"
                                                  onClick={() => edit(row)}>
                                                Редактировать
                                            </Link>
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Sheet>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {games.filter(p => filterSearch === '' || p.name.includes(filterSearch)).map((listItem) => (
                            <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <Stack spacing={1} direction={'row'}>
                                            <Avatar
                                                variant="outlined"
                                                size="sm"
                                                src={listItem.icon}
                                            />
                                            <div style={!listItem.isActive ? {backgroundColor: 'rgba(229,72,72,0.2)'} : {}}>
                                                <Typography gutterBottom sx={{fontWeight: 600}}>
                                                    Имя / Роут: {listItem.name} / {listItem.alias}
                                                </Typography>
                                                <Typography level="body-xs">App ID: {listItem.id}</Typography>
                                                <Typography level="body-xs">Порядок: {listItem.sortOrder}</Typography>
                                                <Typography level="body-xs">Context: {listItem.contextId}</Typography>
                                                <Typography
                                                    level="body-xs">Лимит инвентаря: {listItem.inventoryLimit}</Typography>
                                                <Typography
                                                    level="body-xs">Лимит на заказ: {listItem.orderItemsLimit ?? '(не установлен)'}</Typography>
                                                <Typography
                                                    level="body-xs">Офлайн заявки: {listItem.offlineBidsEnabled ? 'Вкл' : 'Выкл'}</Typography>
                                                <Box
                                                    sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                    <Link level="body-sm" component="button"
                                                          onClick={() => edit(listItem)}>
                                                        Редактировать
                                                    </Link>
                                                </Box>
                                            </div>
                                        </Stack>
                                    </ListItemContent>
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                    </Box>
                </>
            }
        </React.Fragment>
    );
}

